import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Plane, Star, Clock } from 'lucide-react';
import { getAllLounges } from '../services/affiliateService';
import './LoungesSection.css';

function LoungesSection() {
  const [lounges, setLounges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadLounges();
  }, []);  // Empty array - load once

  const loadLounges = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllLounges();
      if (response.success) {
        setLounges(response.data);
      }
    } catch (err) {
      console.error('Failed to load lounges:', err);
      setError('Fehler beim Laden der Lounges');
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <section id="lounges" className="section lounges-section">
        <div className="lounges-header">
          <h2 className="section-title">Premium Flughafen-Lounges</h2>
          <p className="section-subtitle">Lädt...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="lounges" className="section lounges-section">
        <div className="lounges-header">
          <h2 className="section-title">Premium Flughafen-Lounges</h2>
          <p className="section-subtitle" style={{ color: 'var(--coral)' }}>{error}</p>
        </div>
      </section>
    );
  }
  
  return (
    <section id="lounges" className="section lounges-section">
      <div className="lounges-header">
        <h2 className="section-title">Premium Flughafen-Lounges</h2>
        <p className="section-subtitle">
          Entspanne vor dem Flug in exklusiven Lounges - mit Priority Pass über American Express Platinum
        </p>
      </div>

      <div className="amex-highlight card">
        <div className="amex-content">
          <div className="amex-icon">
            <CreditCard size={48} />
          </div>
          <div className="amex-info">
            <h3 className="amex-title">American Express Platinum</h3>
            <p className="amex-description">
              Erhalte kostenlosen Zugang zu über 1.300 Flughafen-Lounges weltweit mit Priority Pass.
              Inklusive Reiseversicherung, Status-Upgrades und exklusiven Benefits.
            </p>
            <div className="amex-benefits">
              <div className="amex-benefit">✓ Priority Pass inklusive</div>
              <div className="amex-benefit">✓ Reise- & Auslandskrankenversicherung</div>
              <div className="amex-benefit">✓ Statusupgrades bei Hotels</div>
              <div className="amex-benefit">✓ 200+ Airline-Lounges</div>
            </div>
            <a href="https://www.americanexpress.com/de/kreditkarten/platinum-card/" className="btn-secondary amex-btn" target="_blank" rel="noopener noreferrer">
              <CreditCard size={20} />
              Jetzt beantragen
            </a>
          </div>
        </div>
      </div>

      <div className="lounges-grid">
        {lounges.map((lounge) => (
          <div key={lounge.id} className="lounge-card card">
            <div className="lounge-header-row">
              <div>
                <h3 className="lounge-name">{lounge.name}</h3>
                <div className="lounge-airport">
                  <Plane size={16} />
                  <span>{lounge.airport}</span>
                </div>
                <div className="lounge-terminal">{lounge.terminal}</div>
              </div>
              <div className="lounge-rating">
                <Star size={20} fill="var(--sunny-yellow)" color="var(--sunny-yellow)" />
                <span className="rating-value">{lounge.rating}</span>
              </div>
            </div>

            <div className="lounge-badges">
              {lounge.priority_pass && (
                <span className="lounge-badge priority">Priority Pass</span>
              )}
              {lounge.amex_only && (
                <span className="lounge-badge amex">Amex Only</span>
              )}
            </div>

            <div className="lounge-highlights">
              <h4 className="highlights-title">Highlights:</h4>
              <ul className="highlights-list">
                {lounge.highlights.map((highlight, idx) => (
                  <li key={idx} className="highlight-item">
                    ✓ {highlight}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lounge-hours">
              <Clock size={16} />
              <span>Öffnungszeiten: {lounge.opening_hours}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="lounges-tip">
        <p>
          💡 <strong>Tipp:</strong> Lade die Priority Pass App herunter, um alle verfügbaren Lounges 
          an deinem Abflughafen zu finden und digitalen Zugang zu erhalten.
        </p>
      </div>
    </section>
  );
}

export default LoungesSection;
